import { useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/user-context/user-context';
import { UserType } from '../../enums/users';
import Authentication from '../authentication/authentication';
import PageNotFound from '../common/page-not-found/page-not-found';
import Branch from '../delivery-company-components/branch/branch';
import Branches from '../delivery-company-components/branches/branches';
import Customer from '../common/customer/customer';
import Customers from '../delivery-company-components/customers/customers';
import Driver from '../delivery-company-components/driver/driver';
import Drivers from '../delivery-company-components/drivers/drivers';
import Employee from '../delivery-company-components/employee/employee';
import Employees from '../delivery-company-components/employees/employees';
import Home from '../common/home/home';
import Merchant from '../delivery-company-components/merchant/merchant';
import Merchants from '../delivery-company-components/merchants/merchants';
import DriverInvoices from '../driver-components/driver-invoices/driver-invoices';
import DriverOrderLookup from '../driver-components/driver-order-lookup/driver-order-lookup';
import MerchantInvoices from '../merchant-components/merchant-invoices/merchant-invoices';
import MerchantOrder from '../merchant-components/merchant-order/order';
import MerchantOrders from '../merchant-components/merchant-orders/merchant-orders';
import './main.css';
import LabelsListing from '../delivery-company-components/labels/labels-listing/labels-listing';
import Labels from '../delivery-company-components/labels/labels';
import Label from '../delivery-company-components/label/label';
import Settings from '../delivery-company-components/settings';
import MerchantPickups from '../merchant-components/merchant-pickup-locations/merchant-pickup';
import MerchantPickup from '../merchant-components/merchant-pickup-location/merchant-pickup';
import MerchantTasks from '../merchant-components/merchant-tasks/merchant-tasks';
import DriverOrdersNew from '../driver-components/driver-pickups/driver-pickups';
import PickupTasks from '../delivery-company-components/pickup-tasks/pickup-tasks';
import Order from '../delivery-company-components/order/order';
import Orders from '../delivery-company-components/orders/orders';
import DriverPickupTaskPageOnlyOrder from '../driver-components/driver-pickups/driver-pickup-task';
import Accounting from '../delivery-company-components/accouting/accounting';
import DriverPaymentTasks from '../driver-components/driver-payment-tasks/driver-payment-tasks';
import WarehouseShelves from '../delivery-company-components/warehouse-shelves/shelves';
import DriverDeliveryBatches from '../driver-components/driver-delivery-tasks/driver-delivery-tasks';
import DeliveryBatches from '../delivery-company-components/delivery-tasks/delivery-tasks';
import DriverMenuGrid from '../driver-components/home/home';
import SimpleBottomNavigation from '../app-layout/bottom-nav/bottom-nav';
import DriverProfile from '../driver-components/profile/profile';
import {
  Page,
  user_can_access_home,
  user_has_page_access,
  user_is_admin,
  user_is_driver,
  user_is_merchant,
} from '../../utils/permissions';
import DocumentationViewer from '../common/documentation-viewer/documentation-viewer';
import MerchantCustomers from '../merchant-components/customers/customers';
import WhatsappConnection from '../driver-components/whatsapp/whatsapp';
import APIIntegrations from '../merchant-components/api-integrations/api-integrations';
import MerchantSettings from '../merchant-components/settings/settings';
import { InternalTransfersPage } from '../delivery-company-components/internal-transfer';
const PrivateRoutes = () => {
  const { user, config } = useContext(AuthContext);
  if (user === null) return <Navigate to="/login" replace />;
  return <Outlet />;
};

const getDefaultRoute = (userType: string): string => {
  switch (userType) {
    case UserType.DRIVER:
      return '/home';
    case UserType.EMPLOYEE:
      return '/orders';
    case UserType.MERCHANT:
      return '/home';
    case UserType.ORGANISATION:
      return '/home';
    default:
      return '/';
  }
};

const ADMIN_ONLY: string[] = [UserType.ORGANISATION];
const EMPLOYEE_OR_ADMIN: string[] = [UserType.ORGANISATION, UserType.EMPLOYEE];
const MERCHANT_ONLY: string[] = [UserType.MERCHANT];
const DRIVER_ONLY: string[] = [UserType.DRIVER];

function MainContent() {
  const { user, config } = useContext(AuthContext);
  let userType = '';

  if (user) userType = user.type;
  else {
    const location = useLocation();
    const from = location.pathname + location.search;
    console.log('Redirecting to login from:', from);
    return (
      <Routes>
        {/* Authentication routes */}
        <Route path="/login/:org" element={<Authentication mode="signin" />} />
        <Route path="/login" element={<Authentication mode="signin" />} />
        <Route path="/signup" element={<Authentication mode="signup" />} />
        <Route
          path="/reset-password"
          element={<Authentication mode="reset" />}
        />

        {/* Default route */}
        <Route path="*" element={<Navigate to={`/login?redirect_to=${encodeURIComponent(from)}`} replace />} />
      </Routes>
    );
  }

  return (
    <div className="main-content">
      <Routes>
        <Route path="/login" element={<Authentication mode="signin" />} />
        <Route path="/signup" element={<Authentication mode="signup" />} />
        <Route
          path="/reset-password"
          element={<Authentication mode="reset" />}
        />
        <Route element={<PrivateRoutes />}>
          {/* Default route */}
          <Route
            path="/"
            element={<Navigate to={getDefaultRoute(user.type)} replace />}
          />
          <Route path="/documentation" element={<DocumentationViewer />} />
         
          {user_can_access_home(user) && <>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
          </>}

          {
            user_has_page_access(user, Page.EMPLOYEES) && <>
              <Route path="/employees" element={<Employees />} />
              <Route path="/employee">
                <Route path=":employeeId" element={<Employee />} />
              </Route>
            </>
          }
          
          {user_is_admin(user) &&  <Route path="/settings" element={<Settings />} />}

          {EMPLOYEE_OR_ADMIN.includes(userType) && (
            <>
              {/* Orders */}
              {user_has_page_access(user, Page.ORDERS) && (
                <>
                  <Route path="/orders" element={<Orders />} />

                  <Route path="/order">
                    <Route path=":orderId" element={<Order />} />
                  </Route>
                </>
              )}

              {/* Customers */}
              {user_has_page_access(user, Page.CUSTOMERS) && <>
                <Route path="/customers" element={<Customers />} />
                <Route path="/customer">
                  <Route path=":customerId" element={<Customer />} />
                </Route>
              </>}
              

              {/* Tasks */}
              {user_has_page_access(user, Page.PICKUP_TASKS) && <Route path="/pickup-tasks" element={<PickupTasks />} />}
              
              {user_has_page_access(user, Page.DELIVERY_BATCHES) && <Route path="/delivery-batches" element={<DeliveryBatches />} />}

              <Route path="/internal-transfers" element={<InternalTransfersPage />} />
              {/* Merchants */}
              {user_has_page_access(user, Page.MERCHANTS) && (
                <>
                  <Route path="/merchants" element={<Merchants />} />

                  <Route path="/merchant">
                    <Route path=":merchantId" element={<Merchant />} />
                  </Route>
                </>
              )}

              {/* Labels */}
              {user_has_page_access(user, Page.LABELS) && (
                <>
                  <Route path="/labels" element={<Labels />} />
                  <Route path="/label">
                    <Route path=":labelId" element={<Label />} />
                  </Route>
                </>
              )}

              {user_has_page_access(user, Page.SHELVES) && <Route path="/shelves" element={<WarehouseShelves />} />}

              {/* Drivers */}
              {user_has_page_access(user, Page.DRIVERS) && (
                <>
                  <Route path="/drivers" element={<Drivers />} />
                  <Route path="/driver">
                    <Route path=":driverId" element={<Driver />} />
                  </Route>
                </>
              )}

              {/* Accounting */}
              {user_has_page_access(user, Page.ACCOUNTING) && (
                <Route path="/accounting" element={<Accounting />} />
              )}
            </>
          )}

          {user_is_driver(user) && (
            <>
              <Route path="/home" element={<DriverMenuGrid />} />
              <Route path="/invoices" element={<DriverInvoices />} />
              <Route path="/pickup-tasks" element={<DriverOrdersNew />} />
              <Route path="/payment-tasks" element={<DriverPaymentTasks />} />
              <Route
                path="/delivery-batches"
                element={<DriverDeliveryBatches />}
              />
              <Route path="/profile" element={<DriverProfile />} />
              <Route path="/whatsapp" element={<WhatsappConnection />} />
            </>
          )}

          {user_is_merchant(user) && (
            <>
              <Route path="/home" element={<Home />} />
              <Route
                path="/customers"
                element={<MerchantCustomers />}
              />
              <Route path="/customer">
                <Route path=":customerId" element={<Customer />} />
              </Route>
              <Route path="/merchant-orders" element={<MerchantOrders />} />
              <Route path="/merchant-pickups" element={<MerchantPickups />} />
              <Route path="/settings" element={<MerchantSettings />} />

              <Route path="/merchant-pickup">
                <Route path=":pickupId" element={<MerchantPickup />} />
              </Route>
              <Route path="/merchant-tasks" element={<MerchantTasks />} />
              <Route path="/invoices" element={<MerchantInvoices />} />
              <Route path="/order">
                <Route path=":orderId" element={<MerchantOrder />} />
              </Route>
              <Route path="/api-integrations" element={<APIIntegrations />} />
            </>
          )}
        </Route>

        {/* Error route */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </div>
  );
}

export default MainContent;
